/**
 * Interlock global search — the nav-rail "jump to any entity" box.
 *
 * A returning engineer types a ref (SR-FN-006, INT-01, RSK-12) or free text and
 * lands directly on the requirement / interface / trade / component / function /
 * risk. Backed by `GET /api/projects/:id/search` (see interlockApi
 * `searchProjectEntities`), which is project-scoped server-side — this UI never
 * requests, and cannot surface, another project's entities.
 *
 * Accessibility: a real labelled `<input>` implementing the ARIA combobox
 * pattern — `role="combobox"`, `aria-expanded`, `aria-controls`,
 * `aria-activedescendant`, and a `role="listbox"` of `role="option"` rows.
 * Keyboard: ↑/↓ move the active option, Enter navigates to it, Escape closes
 * (then clears). Results are debounced (250ms) and grouped by kind.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ScrollText,
  Grid3x3,
  GitBranch,
  Box,
  Workflow,
  ShieldAlert,
  FileQuestion,
  AlertTriangle,
  Gavel,
  type LucideIcon,
} from 'lucide-react';
import {
  searchProjectEntities,
  confirmSpaNavigation,
  type SearchHit,
  type SearchKind,
} from '../../api/interlockApi';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

/**
 * Per-kind display metadata (heading label + glyph), in the same order the API
 * returns. Exported so the `/interlock/search` full-results page (SearchResultsPage)
 * can render the identical grouping/iconography instead of duplicating it.
 */
export const SEARCH_KIND_META: Record<SearchKind, { label: string; Icon: LucideIcon }> = {
  requirement: { label: 'Requirements', Icon: ScrollText },
  interface: { label: 'Interfaces', Icon: Grid3x3 },
  trade: { label: 'Trades', Icon: GitBranch },
  component: { label: 'Components', Icon: Box },
  function: { label: 'Functions', Icon: Workflow },
  risk: { label: 'Risks', Icon: ShieldAlert },
  dataRequest: { label: 'Data Requests', Icon: FileQuestion },
  failureMode: { label: 'Failure Modes', Icon: AlertTriangle },
  decision: { label: 'Decisions', Icon: Gavel },
};
const KIND_META = SEARCH_KIND_META;

export const SEARCH_KIND_ORDER: SearchKind[] = [
  'requirement',
  'interface',
  'trade',
  'dataRequest',
  'component',
  'function',
  'risk',
  'failureMode',
  'decision',
];
const KIND_ORDER = SEARCH_KIND_ORDER;

export function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  // Debounce the raw input into the term we actually query with.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  // Fetch when the debounced term is long enough. A stale-guard drops
  // out-of-order responses so the list always reflects the latest query.
  useEffect(() => {
    if (debounced.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    searchProjectEntities(debounced)
      .then((r) => {
        if (!live) return;
        setResults(r.results);
        setActiveIndex(r.results.length ? 0 : -1);
      })
      .catch(() => {
        if (live) setResults([]);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [debounced]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = useCallback(
    (hit: SearchHit | undefined) => {
      if (!hit) return;
      if (!confirmSpaNavigation()) return;
      setOpen(false);
      setTerm('');
      setDebounced('');
      setResults([]);
      inputRef.current?.blur();
      navigate(hit.url);
    },
    [navigate]
  );

  /** Full-results page (SearchResultsPage) — shareable/bookmarkable, and the
   * only place to see a hit whose kind ran past the dropdown's per-kind cap. */
  const goToFullResults = useCallback(
    (q: string) => {
      const term = q.trim();
      if (term.length < MIN_CHARS) return;
      if (!confirmSpaNavigation()) return;
      setOpen(false);
      inputRef.current?.blur();
      navigate(`/interlock/search?q=${encodeURIComponent(term)}`);
    },
    [navigate]
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open) {
        setOpen(false);
      } else {
        setTerm('');
        setDebounced('');
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => (results.length ? (i + 1) % results.length : -1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : -1));
      return;
    }
    if (e.key === 'Enter') {
      if (activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        go(results[activeIndex]);
      } else if (debounced.length >= MIN_CHARS) {
        // No option highlighted (or none returned) — Enter still has somewhere
        // useful to go: the full, shareable results page for the typed term.
        e.preventDefault();
        goToFullResults(debounced);
      }
    }
  };

  // Group hits by kind while remembering each hit's flat index (for keyboard nav
  // + aria-activedescendant), so the visual grouping and the linear cursor agree.
  const groups = useMemo(() => {
    const byKind = new Map<SearchKind, { hit: SearchHit; index: number }[]>();
    results.forEach((hit, index) => {
      const arr = byKind.get(hit.kind) ?? [];
      arr.push({ hit, index });
      byKind.set(hit.kind, arr);
    });
    return KIND_ORDER.filter((k) => byKind.has(k)).map((kind) => ({
      kind,
      items: byKind.get(kind)!,
    }));
  }, [results]);

  const showPanel = open && debounced.length >= MIN_CHARS;
  const hasResults = results.length > 0;

  return (
    <div className="ilk-search" ref={wrapRef}>
      <label className="ilk-search-label" htmlFor={`${baseId}-input`}>
        <Search className="ilk-search-icon" size={14} aria-hidden="true" />
        <span className="ilk-visually-hidden">Search this project</span>
        <input
          id={`${baseId}-input`}
          ref={inputRef}
          type="text"
          className="ilk-search-input"
          placeholder="Search…"
          autoComplete="off"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showPanel && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </label>

      {showPanel && (
        <div className="ilk-search-panel">
          <ul className="ilk-search-listbox" id={listboxId} role="listbox" aria-label="Search results">
            {loading && !hasResults && (
              <li className="ilk-search-status" role="presentation">
                Searching…
              </li>
            )}
            {!loading && !hasResults && (
              <li className="ilk-search-status" role="presentation">
                No matches for “{debounced}”
              </li>
            )}
            {groups.map(({ kind, items }) => {
              const { label, Icon } = KIND_META[kind];
              return (
                <li key={kind} role="presentation" className="ilk-search-group">
                  <div className="ilk-search-group-head" role="presentation">
                    <Icon size={12} aria-hidden="true" />
                    <span>{label}</span>
                  </div>
                  <ul role="presentation" className="ilk-search-group-list">
                    {items.map(({ hit, index }) => (
                      <li
                        key={`${hit.kind}-${hit.id}`}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        className={`ilk-search-option${index === activeIndex ? ' is-active' : ''}`}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(e) => {
                          // Prevent the input blur from closing the panel before the click lands.
                          e.preventDefault();
                          go(hit);
                        }}
                      >
                        <span className="ilk-search-option-main">
                          {hit.refId && <span className="ilk-search-ref">{hit.refId}</span>}
                          <span className="ilk-search-option-label">{hit.label}</span>
                        </span>
                        {hit.sublabel && (
                          <span className="ilk-search-option-sub">{hit.sublabel}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
          {/* Full-results page — the only reachable view once a kind runs past
              the dropdown's own per-kind cap, and the one shareable/bookmarkable
              URL for a search (`/interlock/search?q=`). Shown whenever there's a
              real term to search, regardless of whether the dropdown itself
              found anything (a currently-empty dropdown doesn't mean the fuller
              page will be too — it applies the same per-kind cap independently). */}
          <button
            type="button"
            className="ilk-search-viewall"
            onMouseDown={(e) => {
              e.preventDefault();
              goToFullResults(debounced);
            }}
          >
            See all results for “{debounced}” →
          </button>
        </div>
      )}
    </div>
  );
}
